import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useInsightsStream } from '../../hooks/useInsightsStream';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useUrlSearchParam } from '../../hooks/useUrlSearchParam';
import { getToken } from '../../utils/token';
import { config } from '../../config';
import { copyToClipboard } from '../../utils/clipboard';
import { DataDictionaryModal } from './DataDictionaryModal';
import { quickPrompts } from './quickPrompts';
import { useInsightsThreadHistory } from './hooks/useInsightsThreadHistory';
import { useInsightsMentions } from './hooks/useInsightsMentions';
import { useInsightsReportIssue } from './hooks/useInsightsReportIssue';
import { useInsightsSqlResults } from './hooks/useInsightsSqlResults';
import { useInsightsThreadOwner } from './hooks/useInsightsThreadOwner';
import { InsightsChatMessages } from './components/InsightsChatMessages';
import { InsightsHeader } from './components/InsightsHeader';
import { InsightsInputComposer } from './components/InsightsInputComposer';
import { InsightsReportIssueModal } from './components/InsightsReportIssueModal';
import { InsightsSidebar } from './components/InsightsSidebar';
import { InsightsToolFeedbackPanel } from './components/InsightsToolFeedbackPanel';
import { deriveToolFeedbackState, type ToolFeedback } from './insightsUtils';
import type { QuickPrompt } from './InsightsPage.types';
import './InsightsPage.css';
import './DataDictionaryModal.css';

const DEFAULT_PANEL_WIDTH = 500;
const TEXTAREA_MAX_HEIGHT = 200;
const COPY_RESET_DELAY_MS = 2000;

export const InsightsPage = () => {
  const [threadId, onThreadId] = useUrlSearchParam('threadId');
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    return !window.matchMedia('(max-width: 1024px)').matches;
  });
  const [isDataDictionaryOpen, setIsDataDictionaryOpen] = useState<boolean>(false);
  const [focusedSqlResultId, setFocusedSqlResultId] = useState<string | null>(null);
  const [panelWidth, setPanelWidth] = useState<number>(DEFAULT_PANEL_WIDTH);
  const [inputValue, setInputValue] = useState<string>('');
  const [webSearchEnabled, setWebSearchEnabled] = useState<boolean>(false);
  const [debugCopiedKey, setDebugCopiedKey] = useState<string | null>(null);
  const [isDebugExpanded, setIsDebugExpanded] = useState<boolean>(false);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef<boolean>(true);
  const scrollOnNextMessageRef = useRef<boolean>(false);
  const debugCopyResetRef = useRef<number | null>(null);

  usePageTitle('Insights - AI Assistant');

  const { threadHistory, isLoadingHistory: isLoadingThreadHistory, refreshThreadHistory } = useInsightsThreadHistory();

  const insightsHeaders = useCallback((): Record<string, string> => {
    const token = getToken();
    if (!token) {
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const thread = useInsightsStream({
    apiUrl: config.INSIGHTS_AGENT_URL,
    threadId,
    onThreadId,
    onThreadCreated: refreshThreadHistory,
    headers: insightsHeaders,
  });

  const { isViewingOtherUsersThread, threadOwnerEmail } = useInsightsThreadOwner({
    ownerAuthServiceUuid: thread.ownerAuthServiceUuid,
  });

  const { threadSqlResults, sqlResultIdsByAiMessageId, sqlResultById, hasUnseenDataResults } = useInsightsSqlResults({
    messages: thread.messages,
    threadId,
    isDataDictionaryOpen,
    isThreadLoading: thread.isLoading,
    isThreadHistoryLoading: thread.isLoadingHistory,
  });

  const mentions = useInsightsMentions({
    inputValue,
    setInputValue,
    inputRef,
  });
  const { closeAllMentionMenus } = mentions;

  const issueReport = useInsightsReportIssue({
    threadId,
  });

  useEffect(() => {
    return () => {
      if (debugCopyResetRef.current) {
        window.clearTimeout(debugCopyResetRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handler = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      if (inputContainerRef.current && !inputContainerRef.current.contains(target)) {
        closeAllMentionMenus();
      }
    };

    document.addEventListener('mousedown', handler);

    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [closeAllMentionMenus]);

  const updateIsAtBottom = useCallback((): void => {
    const container = messagesContainerRef.current;
    if (!container) {
      isAtBottomRef.current = true;
      return;
    }

    const thresholdPx = 10;
    const distanceFromBottom = container.scrollHeight - (container.scrollTop + container.clientHeight);
    isAtBottomRef.current = distanceFromBottom <= thresholdPx;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'instant'): void => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }, []);

  useEffect(() => {
    updateIsAtBottom();

    if (scrollOnNextMessageRef.current) {
      scrollToBottom('instant');
      scrollOnNextMessageRef.current = false;
      return;
    }

    if (thread.isLoading) {
      return;
    }

    if (isAtBottomRef.current) {
      scrollToBottom('smooth');
    }
  }, [scrollToBottom, thread.isLoading, thread.messages, updateIsAtBottom]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();

      if (!inputValue.trim() || thread.isLoading) {
        return;
      }

      scrollOnNextMessageRef.current = true;

      thread.submit({
        messages: [
          {
            type: 'human',
            content: inputValue,
          },
        ],
        webSearchEnabled,
      });

      setInputValue('');

      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
    },
    [inputValue, thread, webSearchEnabled],
  );

  const handleQuickPrompt = useCallback(
    (prompt: QuickPrompt): void => {
      if (!prompt.input.trim() || thread.isLoading) {
        return;
      }

      setInputValue(prompt.input);
      scrollOnNextMessageRef.current = false;

      void requestAnimationFrame(() => {
        inputRef.current?.focus();
        const placeholderMatch = prompt.input.match(/<[^>]+>/);
        if (!placeholderMatch || placeholderMatch.index == null) {
          return;
        }

        const start = placeholderMatch.index;
        const end = start + placeholderMatch[0].length;
        inputRef.current?.setSelectionRange(start, end);
      });
    },
    [thread.isLoading],
  );

  const handleNewChat = useCallback((): void => {
    thread.stop();
    onThreadId(null);
    setInputValue('');
  }, [onThreadId, thread]);

  const handleSelectThread = useCallback(
    (selectedThreadId: string): void => {
      if (selectedThreadId === threadId) {
        return;
      }

      thread.stop();
      onThreadId(selectedThreadId);
    },
    [onThreadId, thread, threadId],
  );

  const handleToggleDataDictionary = useCallback((): void => {
    setFocusedSqlResultId(null);
    setIsDataDictionaryOpen((prev) => !prev);
  }, []);

  const handleOpenDataDictionary = useCallback((): void => {
    setFocusedSqlResultId(null);
    setIsDataDictionaryOpen(true);
  }, []);

  const handleExploreDataForResult = useCallback((resultId: string | null): void => {
    setFocusedSqlResultId(resultId);
    setIsDataDictionaryOpen(true);
  }, []);

  const handleJumpToMessage = useCallback((messageId: string): void => {
    const target = document.getElementById(`insights-message-${messageId}`);
    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleCopyMessage = useCallback((messageText: string): void => {
    void copyToClipboard(messageText).catch(() => undefined);
  }, []);

  const handleCopyToolFeedback = useCallback(async (feedback: ToolFeedback): Promise<void> => {
    try {
      const copied = await copyToClipboard(feedback.details);
      if (!copied) {
        return;
      }

      setDebugCopiedKey(feedback.key);
      if (debugCopyResetRef.current !== null) {
        window.clearTimeout(debugCopyResetRef.current);
      }

      debugCopyResetRef.current = window.setTimeout(() => {
        setDebugCopiedKey(null);
      }, COPY_RESET_DELAY_MS);
    } catch {
      setDebugCopiedKey(null);
    }
  }, []);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>): void => {
      const nextValue = event.target.value;
      const cursor = event.target.selectionStart ?? nextValue.length;
      mentions.handleInputChange(nextValue, cursor);

      event.target.style.height = 'auto';
      event.target.style.height = `${Math.min(event.target.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
    },
    [mentions],
  );

  const handleInputClick = useCallback(
    (event: ReactMouseEvent<HTMLTextAreaElement>): void => {
      const cursor = event.currentTarget.selectionStart ?? inputValue.length;
      mentions.handleCursorChange(inputValue, cursor);
    },
    [inputValue, mentions],
  );

  const handleInputKeyUp = useCallback(
    (event: ReactKeyboardEvent<HTMLTextAreaElement>): void => {
      const cursor = event.currentTarget.selectionStart ?? inputValue.length;
      mentions.handleCursorChange(inputValue, cursor);
    },
    [inputValue, mentions],
  );

  const handleInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLTextAreaElement>): void => {
      const handlingResult = mentions.handleKeyDown(event);
      if (handlingResult !== 'submit') {
        return;
      }

      if (!inputValue.trim() || thread.isLoading) {
        return;
      }

      const form = event.currentTarget.closest('form');
      form?.requestSubmit();
    },
    [inputValue, mentions, thread.isLoading],
  );

  const { toolFeedback, currentActivity } = useMemo(
    () => deriveToolFeedbackState(thread.messages, thread.isLoading),
    [thread.isLoading, thread.messages],
  );

  return (
    <>
      <div className={`insights-container ${isSidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'}`}>
        <InsightsSidebar
          isSidebarOpen={isSidebarOpen}
          isLoadingHistory={isLoadingThreadHistory}
          threadHistory={threadHistory}
          activeThreadId={threadId}
          onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
          onNewChat={handleNewChat}
          onSelectThread={handleSelectThread}
        />

        <div className="insights-main" style={isDataDictionaryOpen ? { marginRight: panelWidth } : undefined}>
          <InsightsHeader
            isViewingOtherUsersThread={isViewingOtherUsersThread}
            threadOwnerEmail={threadOwnerEmail}
            isDataDictionaryOpen={isDataDictionaryOpen}
            hasUnseenDataResults={hasUnseenDataResults}
            onToggleDataDictionary={handleToggleDataDictionary}
          />

          <InsightsChatMessages
            thread={thread}
            quickPrompts={quickPrompts}
            currentActivity={currentActivity}
            messagesContainerRef={messagesContainerRef}
            messagesEndRef={messagesEndRef}
            sqlResultIdsByAiMessageId={sqlResultIdsByAiMessageId}
            sqlResultById={sqlResultById}
            feedbackEnabled={config.INSIGHTS_FEEDBACK_ENABLED}
            onScroll={updateIsAtBottom}
            onQuickPrompt={handleQuickPrompt}
            onOpenDataDictionary={handleOpenDataDictionary}
            onExploreDataForResult={handleExploreDataForResult}
            onOpenReportIssue={issueReport.openReportIssue}
            onCopyMessage={handleCopyMessage}
          />

          <InsightsToolFeedbackPanel
            currentActivity={currentActivity}
            toolFeedback={toolFeedback}
            isDebugExpanded={isDebugExpanded}
            debugCopiedKey={debugCopiedKey}
            onToggleDebug={() => setIsDebugExpanded((prev) => !prev)}
            onCopyDetails={handleCopyToolFeedback}
          />

          <InsightsInputComposer
            inputValue={inputValue}
            inputRef={inputRef}
            inputContainerRef={inputContainerRef}
            isThreadLoading={thread.isLoading}
            webSearchEnabled={webSearchEnabled}
            userSearchResults={mentions.userSearchResults}
            isUserSearchLoading={mentions.isUserSearchLoading}
            isUserSearchOpen={mentions.isUserSearchOpen}
            userSearchActiveIndex={mentions.userSearchActiveIndex}
            divisionSearchResults={mentions.divisionSearchResults}
            isDivisionSearchLoading={mentions.isDivisionSearchLoading}
            isDivisionSearchOpen={mentions.isDivisionSearchOpen}
            divisionSearchActiveIndex={mentions.divisionSearchActiveIndex}
            marketSearchResults={mentions.marketSearchResults}
            isMarketSearchLoading={mentions.isMarketSearchLoading}
            isMarketSearchOpen={mentions.isMarketSearchOpen}
            marketSearchActiveIndex={mentions.marketSearchActiveIndex}
            onSubmit={handleSubmit}
            onToggleWebSearch={() => setWebSearchEnabled((prev) => !prev)}
            onInputChange={handleInputChange}
            onInputClick={handleInputClick}
            onInputKeyUp={handleInputKeyUp}
            onInputKeyDown={handleInputKeyDown}
            onOpenDataDictionary={handleOpenDataDictionary}
            onSelectUserResult={mentions.selectUserResult}
            onSelectDivisionResult={mentions.selectDivisionResult}
            onSelectMarketResult={mentions.selectMarketResult}
            onStop={() => thread.stop()}
          />
        </div>

        <DataDictionaryModal
          isOpen={isDataDictionaryOpen}
          onClose={() => {
            setIsDataDictionaryOpen(false);
            setFocusedSqlResultId(null);
          }}
          sqlResults={threadSqlResults}
          onWidthChange={setPanelWidth}
          onJumpToMessage={handleJumpToMessage}
          focusResultId={focusedSqlResultId}
        />
      </div>

      <InsightsReportIssueModal
        reportIssueMessageId={issueReport.reportIssueMessageId}
        reportIssueType={issueReport.reportIssueType}
        reportIssueDescription={issueReport.reportIssueDescription}
        isSubmittingReport={issueReport.isSubmittingReport}
        reportSubmitStatus={issueReport.reportSubmitStatus}
        onReportIssueTypeChange={issueReport.setReportIssueType}
        onReportIssueDescriptionChange={issueReport.setReportIssueDescription}
        onClose={issueReport.closeReportIssue}
        onSubmit={() => {
          void issueReport.submitReport();
        }}
      />
    </>
  );
};
